import { __decorate } from "tslib";
import { NumberField } from '@microsoft/fast-foundation';
import { attr } from '@microsoft/fast-element';
import { ChameleonElementMixin } from '../common/mixin';
import { DEV } from 'esm-env';
import { uniqueId } from '@microsoft/fast-web-utilities';
export class NumberFieldComponent extends ChameleonElementMixin(NumberField) {
    constructor() {
        super(...arguments);
        this.fieldsize = 'medium';
        this.success = false;
        this.error = false;
        this.optional = false;
        this.infoContainer = document.createElement('div');
        this.helperTextEl = document.createElement('slot');
        this.handleHelperTextSlotChange = () => {
            const helperText = this.helperTextEl.assignedElements()[0];
            if (!helperText || !helperText.textContent) {
                return;
            }
            this.control.setAttribute('aria-describedby', helperText.id);
        };
    }
    connectedCallback() {
        super.connectedCallback();
        this.control?.setAttribute('type', 'number');
        const helperText = this.shadowRoot?.querySelector('[name="helpertext"]');
        if (!helperText) {
            const slot = document.createElement('slot');
            slot.name = 'helpertext';
            this.shadowRoot?.appendChild(slot);
        }
        this.addHelperText();
        this.addIdForLabel();
        this.setAccessibleLabel();
    }
    addHelperText() {
        this.helperTextEl.setAttribute('name', 'helpertext');
        this.helperTextEl.setAttribute('id', uniqueId('helpertext-'));
        this.helperTextEl.addEventListener('slotchange', this.handleHelperTextSlotChange);
        const helperText = this.shadowRoot?.querySelector('[name="helpertext"]');
        if (!helperText) {
            this.infoContainer?.prepend(this.helperTextEl);
        }
    }
    attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        if (name === 'readonly' && this.hasAttribute('readonly')) {
            this.setAttribute('aria-readonly', 'true');
        }
        else if (name === 'readonly' && !this.hasAttribute('readonly')) {
            this.removeAttribute('aria-readonly');
        }
        this.addAllRelevantAttributes();
        if (this.hasAttribute('disabled')) {
            requestAnimationFrame(() => {
                this.control?.removeAttribute('disabled');
            });
        }
        this.control?.setAttribute('aria-disabled', `${this.hasAttribute('disabled')}`);
        this.control?.addEventListener('keydown', (event) => {
            if (this.hasAttribute('disabled') && event.key !== 'Tab') {
                event.preventDefault();
            }
        });
    }
    addAllRelevantAttributes() {
        const attributes = this.getAttributeNames();
        attributes.forEach((attribute) => {
            if (Object.keys(HTMLInputElement.prototype).includes(attribute)) {
                this.control?.setAttribute(attribute, this.getAttribute(attribute));
                if (attribute === 'disabled') {
                    this.control?.removeAttribute('disabled');
                    this.control?.setAttribute('aria-disabled', `${this.hasAttribute('disabled')}`);
                }
            }
        });
        if (this.optional || this.textContent?.includes('(optional)')) {
            this.control?.removeAttribute('aria-required');
        }
        else {
            this.control?.setAttribute('aria-required', 'true');
        }
    }
    addIdForLabel() {
        const label = this.shadowRoot?.querySelector('label');
        if (this.labelId) {
            this.setAttribute('aria-labelledby', this.labelId);
        }
        else if (label) {
            label.id = uniqueId('label-');
            this.control.setAttribute('aria-labelledby', label.id);
        }
    }
    setAccessibleLabel() {
        if (this.textContent?.trim()) {
            return;
        }
        if (this.labelId) {
            this.setAttribute('aria-labelledby', this.labelId);
            return;
        }
        if (DEV) {
            this.validateAccessibleLabel();
        }
    }
    validateAccessibleLabel() {
        if (!this.getAttribute('aria-labelledby')?.length) {
            console.error(`CHAMELEON-A11Y: NumberField component must have an accessible label provided by either   
  1) text content 
  2) a visible label whose id is given as the attribute labelId
`);
        }
    }
}
__decorate([
    attr
], NumberFieldComponent.prototype, "fieldsize", void 0);
__decorate([
    attr({ mode: 'boolean' })
], NumberFieldComponent.prototype, "success", void 0);
__decorate([
    attr({ mode: 'boolean' })
], NumberFieldComponent.prototype, "error", void 0);
__decorate([
    attr({ mode: 'boolean' })
], NumberFieldComponent.prototype, "optional", void 0);
__decorate([
    attr({ attribute: 'label-id' })
], NumberFieldComponent.prototype, "labelId", void 0);
